import ToggleSwitch from "@src/app/components/ToggleSwitch/ToggleSwitch";
import VideoPlayer from "@src/app/components/VideoPlayer/VideoPlayer";
import VideoWidget from "@src/app/components/VideoWidget/VideoWidget";
import type { BFFArticleCard } from "@src/app/types/bff";
import type { MouseEvent } from "react";
import {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

function formatTime(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}

type Props = {
  articles: BFFArticleCard[];
  currentVideo?: number;
};

export default function VideoPlayerWithWidgets({
  articles,
  currentVideo = 0,
}: Props): ReactElement {
  // Refs for video player and autoplay switch
  const videoPlayerRef = useRef<HTMLDivElement>(null);
  const autoplaySwitchRef = useRef<HTMLDivElement>(null);
  const autoplayStateRef = useRef(true);

  // State management
  const [videoPlayerHeight, setVideoPlayerHeight] = useState<number>(0);
  const [autoplaySwitchHeight, setAutoplaySwitchHeight] = useState<number>(0);

  const [reorderedArticles, setReorderedArticles] =
    useState<BFFArticleCard[]>(articles);
  const [selectedVideoIndex, setSelectedVideoIndex] =
    useState<number>(currentVideo);
  const [isAutoplay, setIsAutoplay] = useState<boolean>(true);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [isEnded, setIsEnded] = useState<boolean>(false);

  const selectedVideoArticle = reorderedArticles[selectedVideoIndex];

  // Widget click handler & video controls handlers
  const onWidgetClick = (index: number) => {
    if (index === 0) {
      setSelectedVideoIndex(0);
      return;
    }
    const updated = [...reorderedArticles];
    const [clicked] = updated.splice(index, 1);
    updated.unshift(clicked);
    setReorderedArticles(updated);
    setSelectedVideoIndex(0);
  };

  const handlePreviousVideo = () => {
    setSelectedVideoIndex(selectedVideoIndex - 1);
  };

  const handleNextVideo = useCallback(() => {
    setSelectedVideoIndex((prevIndex) => {
      if (isAutoplay && prevIndex + 1 === articles.length) {
        // Reset to first video if autoplay is on and last video is reached
        return 0;
      } else {
        return prevIndex + 1;
      }
    });
  }, [isAutoplay, articles.length]);

  // Update heights of video player and autoplay switch on mount and window resize
  useEffect(() => {
    const updateHeights = () => {
      if (videoPlayerRef.current) {
        setVideoPlayerHeight(videoPlayerRef.current.clientHeight);
      }

      // Only need to set this once
      if (autoplaySwitchRef.current && autoplaySwitchHeight === 0) {
        setAutoplaySwitchHeight(autoplaySwitchRef.current.clientHeight);
      }
    };

    // Initial height setup
    updateHeights();

    // Update heights on window resize
    const observer = new ResizeObserver(() => {
      updateHeights(); // only for videoPlayer
    });

    if (videoPlayerRef.current) {
      observer.observe(videoPlayerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [autoplaySwitchHeight]);

  // Keep autoplayStateRef in sync with isAutoplay state
  useEffect(() => {
    autoplayStateRef.current = isAutoplay;

    if (autoplayStateRef.current && isEnded) {
      // If user just turned autoplay ON while a video ended, start next video
      handleNextVideo();
    }
  }, [isAutoplay, isEnded, handleNextVideo]);

  return (
    <div
      id="video-player-with-widgets"
      data-testid="video-player-with-widgets"
      className="bg-background-primary-on-dark lg:grid lg:grid-cols-4"
    >
      <div ref={videoPlayerRef} className="h-max lg:col-span-3">
        <VideoPlayer
          videoArticle={selectedVideoArticle}
          totalVideos={articles.length}
          // isAutoplay={isAutoplay}
          selectedVideoIndex={selectedVideoIndex}
          titleClampLines={3}
          onPreviousVideo={handlePreviousVideo}
          onNextVideo={handleNextVideo}
          onEndedVideo={() => {
            if (autoplayStateRef.current) {
              handleNextVideo();
            } else {
              setIsPlaying(false);
              setIsEnded(true);
            }
          }}
          onPausedVideo={() => {
            setIsPlaying(false);
            setIsPaused(true);
          }}
          onPlayVideo={() => {
            setIsPlaying(true);
            setIsPaused(false);
            setIsEnded(false);
          }}
        />
      </div>
      <div className="col-span-1 hidden lg:block">
        <div
          ref={autoplaySwitchRef}
          className="bg-inverse flex items-center justify-between px-3 py-4"
        >
          <p className="text-label-2 text-secondary-on-dark font-bold">
            Next up
          </p>
          <div className="flex items-center space-x-4">
            <span className="text-label-2 text-secondary-on-dark font-bold">
              Autoplay
            </span>
            <ToggleSwitch
              isEnabled={isAutoplay}
              onChange={(checked: boolean) => {
                setIsAutoplay(checked);
              }}
            />
          </div>
        </div>
        <div
          style={{
            height: `${Math.max(videoPlayerHeight - autoplaySwitchHeight, 0)}px`,
          }}
          className="overflow-y-auto"
        >
          {reorderedArticles.map((article, index) => {
            const timeCode = formatTime(article.youtubeInfo?.duration || 0);
            return (
              <VideoWidget
                key={article.id}
                thumbnailSrc={article.cover}
                timeCode={timeCode}
                title={article.title}
                youtubeSrc={article.youtubeUrl || ""}
                youtubeId={
                  article.youtubeInfo?.id ||
                  article.youtubeUrl?.split("/embed/")[1] ||
                  ""
                }
                isPlaying={isPlaying}
                isPaused={isPaused}
                isEnded={isEnded}
                isSelected={index === selectedVideoIndex}
                data-video={index}
                onClick={(e: MouseEvent<HTMLDivElement>) => {
                  const videoIndex = Number(e.currentTarget.dataset.video);
                  onWidgetClick(videoIndex);
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
